import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, BookOpen, LogOut, User as UserIcon, LayoutDashboard, Shield, Bookmark, Users as UsersIcon, Star } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import NotificationsBell from "@/components/NotificationsBell";

const readerLinks = [
  { to: "/", label: "Home" },
  { to: "/discover", label: "Find My Read" },
  { to: "/library", label: "Library" },
  { to: "/community", label: "Community" },
  { to: "/new-releases", label: "New Releases" },
  { to: "/coming-soon", label: "Coming Soon" },
  { to: "/arc", label: "ARC 💖" },
  { to: "/pricing", label: "Pricing" },
  { to: "/authors", label: "Indie Authors" },
];

const authorLinks = [
  { to: "/", label: "Home" },
  { to: "/discover", label: "Find My Read" },
  { to: "/library", label: "Library" },
  { to: "/author/dashboard", label: "Dashboard" },
  { to: "/submit-book", label: "Submit Book" },
  { to: "/author/promote", label: "Promote" },
  { to: "/community", label: "Community" },
  { to: "/new-releases", label: "New Releases" },
  { to: "/coming-soon", label: "Coming Soon" },
  { to: "/arc", label: "ARC 💖" },
  { to: "/authors", label: "Indie Authors" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAdmin, isAuthor, signOut } = useAuth();
  const navLinks = isAuthor ? authorLinks : readerLinks;

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <Link to="/" className="flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-primary" />
          <span className="font-display text-xl font-bold text-foreground">
            Romance<span className="text-primary">Radar</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location.pathname === link.to ? "text-primary" : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <>
              <Link
                to="/admin"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === "/admin" ? "text-primary" : "text-muted-foreground"
                }`}
              >
                Admin
              </Link>
              <Link
                to="/admin/reports"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === "/admin/reports" ? "text-primary" : "text-muted-foreground"
                }`}
              >
                Reports
              </Link>
              <Link
                to="/admin/promos"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === "/admin/promos" ? "text-primary" : "text-muted-foreground"
                }`}
              >
                Promos
              </Link>
              <Link
                to="/admin/submissions"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === "/admin/submissions" ? "text-primary" : "text-muted-foreground"
                }`}
              >
                Submissions
              </Link>
            </>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <NotificationsBell />
              {isAuthor && (
                <Link
                  to="/author/dashboard"
                  className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
                  title="Author dashboard"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                </Link>
              )}
              <Link
                to="/profile"
                className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
              >
                <UserIcon className="w-3.5 h-3.5" />
                {profile?.display_name ?? user.email}
              </Link>
              <button
                onClick={handleSignOut}
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/auth"
              className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Sign In
            </Link>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="md:hidden text-foreground" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-border"
          >
            <div className="flex flex-col p-4 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`text-sm font-medium ${
                    location.pathname === link.to ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="border-t border-border my-1" />

              {(() => {
                const profileTo = !user ? "/auth" : isAdmin ? "/admin" : isAuthor ? "/author/dashboard" : "/profile";
                const profileLabel = !user
                  ? "Sign In / Profile"
                  : isAdmin
                    ? "Admin Dashboard"
                    : isAuthor
                      ? "Author Dashboard"
                      : "My Profile";
                return (
                  <Link
                    to={profileTo}
                    onClick={() => setOpen(false)}
                    state={!user ? { redirectTo: location.pathname } : undefined}
                    className={`flex items-center gap-3 text-sm font-semibold ${
                      location.pathname === profileTo ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {user ? (
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={profile?.avatar_url ?? undefined} alt={profile?.display_name ?? "Profile"} />
                        <AvatarFallback className="text-xs">
                          {(profile?.display_name ?? user.email ?? "?").slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <UserIcon className="w-5 h-5" />
                    )}
                    <span>{profileLabel}</span>
                  </Link>
                );
              })()}

              {user && !isAdmin && (
                <div className="flex flex-col gap-3 pl-10 -mt-1">
                  <Link
                    to="/library"
                    onClick={() => setOpen(false)}
                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-2"
                  >
                    <Bookmark className="w-3.5 h-3.5" /> Saved & TBR
                  </Link>
                  <Link
                    to="/community"
                    onClick={() => setOpen(false)}
                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-2"
                  >
                    <UsersIcon className="w-3.5 h-3.5" /> Community
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setOpen(false)}
                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-2"
                  >
                    <Star className="w-3.5 h-3.5" /> My Reviews
                  </Link>
                </div>
              )}

              {user && isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 text-sm font-medium text-muted-foreground"
                >
                  <Shield className="w-4 h-4" /> Admin Panel
                </Link>
              )}

              {user ? (
                <button
                  onClick={() => {
                    setOpen(false);
                    handleSignOut();
                  }}
                  className="bg-secondary text-foreground px-5 py-2 rounded-full text-sm font-semibold text-center inline-flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setOpen(false)}
                  className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-semibold text-center"
                >
                  Sign In
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
